import { Button, Icon, Spacing, TableCol, TextInput } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { AccessType, Company } from 'zens-store'
import { useNumberParams, usePaginationParam, Link } from 'zens-router'
import { usePermissions } from '@dms/hook'
import { CompanyPin, CompanyTable, PageHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  table: {
    margin: Spacing.L,
  },
  header: {
    flexDirection: 'row',
  },
  search: {
    minWidth: 300,
    marginRight: Spacing.S,
  },
})

const CompanySelect = () => {
  const [search, setSearch] = useState('')
  const [{ companyId }] = useNumberParams()
  const [pagination, setPagination] = usePaginationParam()
  const { t } = useTranslation()
  const { hasPermission } = usePermissions(companyId)

  const cols: TableCol<Company>[] = [
    {
      key: 'pin',
      title: t('Pin'),
      size: { width: 50 },
      render: (company) => {
        return <CompanyPin company={company} />
      },
      sort: (a, b) => {
        if (a.isPinned === b.isPinned) return 0
        else if (a.isPinned) return 1
        return -1
      },
    },
    {
      key: 'name',
      title: t('Company'),
    },
    {
      key: 'numberOfLocations',
      title: t('Locations'),
    },
    {
      key: 'numberOfWorkspaces',
      title: t('Workspace count'),
    },
  ]

  function updateSearch(search: string) {
    setSearch(search)
    setPagination({ page: 0, size: pagination.size })
  }

  return (
    <View style={styles.container}>
      <PageHeader
        title={t('Select a company')}
        accessory={
          <View style={styles.header}>
            <TextInput
              left="search"
              placeholder={t('Search company name')}
              right={
                search ? (
                  <TouchableOpacity onPress={() => updateSearch('')} testID="CompanySelect.Search.Clear">
                    <Icon name="close" size="S" />
                  </TouchableOpacity>
                ) : undefined
              }
              value={search}
              onChangeText={updateSearch}
              style={styles.search}
              testID="CompanySelect.Search"
            />
            {hasPermission(AccessType.Create, 'company') && (
              <Link href="/companies/add" asChild>
                <Button title={t('Create new company')} left="plus" testID="CompanySelect.Create" />
              </Link>
            )}
          </View>
        }
      />
      <View style={styles.table}>
        <Suspense fallback={<CompanyTable.Skeleton cols={cols} />}>
          <CompanyTable search={search} cols={cols} pagination={pagination} onPaginationChange={setPagination} />
        </Suspense>
      </View>
    </View>
  )
}

export default CompanySelect
